import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  Package,
  MapPin,
  Clock,
  Store,
  User,
  Truck,
  Calendar,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertCircle,
  RotateCcw,
  Phone,
  Receipt,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import useApi from "../../utils/useApi";
import apis from "../../utils/apis";
import { formatPrice, buildCdnURL } from "../../utils/utils";
import Header from "../../components/Header";
import Spinner from "../../components/Spinner";
import CancelOrderModal from "../../components/CancelOrderModal";
import ReturnOrderModal from "../../components/ReturnOrderModal";
import PullToRefresh from "../../components/PullToRefresh";

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Check if user came from orders list
  const fromOrdersList = location.state?.fromOrdersList;
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const contentRef = useRef(null);

  const { callApi } = useApi();
  const { callApi: updateOrder } = useApi();
  const { callApi: cancelReturn } = useApi();

  const OrderStatus = {
    PENDING: "pending",
    PROCESSING: "processing",
    SHIPPED: "shipped",
    DELIVERED: "delivered",
    CANCELLED: "cancelled",
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      if (!isRefreshing) {
        setLoading(true);
      }
      const response = await callApi({
        api: apis.getOrderDetails,
        param: orderId,
      });
      setOrderDetails(response);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error(t("orders.orderDetails.errorLoadingDetails"));
      navigate("/orders", { replace: true });
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const getStatusIcon = (status, hasReturnRequest = false) => {
    if (hasReturnRequest) {
      return <RotateCcw className="w-5 h-5 text-orange-500" />;
    }
    switch (status?.toLowerCase()) {
      case "delivered":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "cancelled":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case "shipped":
        return <Truck className="w-5 h-5 text-blue-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status, hasReturnRequest = false) => {
    if (hasReturnRequest) {
      return "text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400";
    }
    switch (status?.toLowerCase()) {
      case "delivered":
        return "text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400";
      case "cancelled":
        return "text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400";
      case "pending":
        return "text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "shipped":
        return "text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400";
      default:
        return "text-gray-600 bg-gray-50 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  const formatDetailedDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatIndianTime = (timeString) => {
    if (!timeString) return timeString;

    // Parse time string (assuming it's in HH:MM format)
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const minute = parseInt(minutes) || 0;

    // Convert to 12-hour format
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

    return `${displayHour}${minute > 0 ? `:${minute.toString().padStart(2, "0")}` : ""
      } ${period}`;
  };

  const openCancelModal = () => {
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
  };

  const openReturnModal = () => {
    setShowReturnModal(true);
  };

  const closeReturnModal = () => {
    setShowReturnModal(false);
  };

  const handleCancelOrder = async () => {
    try {
      await updateOrder({
        api: apis.updateOrder,
        param: orderDetails._id,
        body: {
          orderStatus: OrderStatus.CANCELLED,
        },
      });
      // Refresh order details
      fetchOrderDetails();
      toast.success(t("orders.orderDetails.orderCancelledSuccess"));
      closeCancelModal();
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error(t("orders.orderDetails.errorCancellingOrder"));
      closeCancelModal();
    }
  };

  const handleCancelReturn = async () => {
    if (!orderDetails?.returnRequest?._id) {
      toast.error(t("orders.orderDetails.errorCancellingReturn"));
      return;
    }

    try {
      await cancelReturn({
        api: apis.cancelReturn,
        param: `${orderDetails.returnRequest._id}/cancel`,
      });
      fetchOrderDetails();
      toast.success(t("orders.orderDetails.returnCancelledSuccess"));
    } catch (error) {
      toast.error(t("orders.orderDetails.errorCancellingReturn"));
    }
  };

  const getStatusProgress = (status) => {
    const statusOrder = [
      OrderStatus.PENDING,
      OrderStatus.PROCESSING,
      OrderStatus.SHIPPED,
      OrderStatus.DELIVERED,
    ];
    const currentIndex = statusOrder.indexOf(status);
    return currentIndex >= 0
      ? ((currentIndex + 1) / statusOrder.length) * 100
      : 0;
  };

  if (loading && !isRefreshing) {
    return <Spinner />;
  }

  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t("orders.orderDetails.orderNotFound")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t("orders.orderDetails.orderNotFoundMessage")}
          </p>
          <button
            onClick={() => navigate("/orders", { replace: true })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t("orders.orderDetails.backToOrders")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="bg-white shadow-sm sticky top-0 z-50 section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <button
            onClick={() => {
              // If user came from orders list, go back with state to use cache
              if (fromOrdersList) {
                navigate("/orders", { state: { fromOrderDetails: true } });
              } else {
                navigate(-1);
              }
            }}
            className="text-gray-600 transition-colors dark:text-foreground-100"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground-100 ml-4">
            {t("orders.orderDetails.myOrder")}
          </h1>
        </div>
      </header>

      <PullToRefresh
        containerRef={contentRef}
        onRefresh={async () => {
          setIsRefreshing(true);
          await fetchOrderDetails();
        }}
      >
        <div className="p-4 mx-auto" ref={contentRef}>
          {/* Order Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="section rounded-xl shadow-sm mb-6"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {t("orders.orderDetails.order")} #{orderDetails.orderId}
                      </h1>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t("orders.orderDetails.placedOn")}{" "}
                      {formatDetailedDate(orderDetails.createdAt)}
                    </p>
                  </div>
                </div>
                <div
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-full ${getStatusColor(
                    orderDetails.orderStatus,
                    orderDetails.returnRequest?.status === "RETURN_REQUESTED"
                  )}`}
                >
                  {getStatusIcon(
                    orderDetails.orderStatus,
                    orderDetails.returnRequest?.status === "RETURN_REQUESTED"
                  )}
                  <span className="text-sm font-medium">
                    {orderDetails.returnRequest?.status === "RETURN_REQUESTED"
                      ? t("orders.status.returnRequested")
                      : t(`orders.status.${orderDetails.orderStatus}`)}
                  </span>
                </div>
              </div>

              {/* Progress Timeline */}
              {orderDetails.orderStatus !== OrderStatus.CANCELLED && (
                <div className="mt-6">
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-green-500 dark:bg-green-400 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${getStatusProgress(
                          orderDetails.orderStatus
                        )}%`,
                      }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>{t("orders.orderDetails.placed")}</span>
                    <span>{t("orders.orderDetails.processing")}</span>
                    <span>{t("orders.orderDetails.shipped")}</span>
                    <span>{t("orders.orderDetails.delivered")}</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6">
                {orderDetails.orderStatus === OrderStatus.PENDING && (
                  <button
                    onClick={openCancelModal}
                    className="w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center space-x-2"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>{t("orders.orderDetails.cancelOrder")}</span>
                  </button>
                )}
                {orderDetails.returnRequest?.status === "RETURN_REQUESTED" && (
                  <button
                    onClick={handleCancelReturn}
                    className="w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center space-x-2"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>{t("orders.orderDetails.cancelReturn")}</span>
                  </button>
                )}
                {orderDetails.orderStatus === OrderStatus.DELIVERED &&
                  !orderDetails.returnRequest && (
                    <button
                      onClick={openReturnModal}
                      className="w-full px-4 py-3 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-orange-800 rounded-lg font-medium hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors flex items-center justify-center space-x-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>{t("orders.orderDetails.returnOrder")}</span>
                    </button>
                  )}
                <a
                  href={`https://bill.kiranapoint.in/#/app-receipt/${orderDetails._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full px-4 py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors flex items-center justify-center space-x-2"
                >
                  <Receipt className="w-4 h-4" />
                  <span>{t("orders.orderDetails.viewTaxInvoice")}</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Products Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="section rounded-xl shadow-sm mb-6"
          >
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2" />
                {t("orders.orderDetails.itemsOrdered")} (
                {orderDetails.products.length})
              </h2>
              <div className="space-y-4">
                {orderDetails.products.map((item) => (
                  <div
                    key={item._id}
                    className="flex items-center space-x-4 p-4  rounded-lg"
                  >
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-600 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={
                          buildCdnURL(item.product.images[0]) ||
                          "/assets/placeholder.webp"
                        }
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                        {item.product.name}
                      </h3>
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <span>
                          {t("orders.orderDetails.qty")} {item.quantity}
                        </span>
                        {item.variant && (
                          <>
                            <span>•</span>
                            <span>
                              {orderDetails.products[0]?.product?.variants?.find(
                                (v) => v._id === item.variant
                              )?.title || "Standard"}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-gray-100">
                        ₹{formatPrice(item.total)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        ₹{formatPrice(item.price)}{" "}
                        {t("orders.orderDetails.each")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Order Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Shipping Address */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="section rounded-xl shadow-sm"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  {t("orders.orderDetails.deliveryAddress")}
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {orderDetails.shippingAddress?.name ||
                      t("orders.orderDetails.notAvailable")}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    {orderDetails.shippingAddress?.address}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    {orderDetails.shippingAddress?.city},{" "}
                    {orderDetails.shippingAddress?.state}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    {orderDetails.shippingAddress?.country} -{" "}
                    {orderDetails.shippingAddress?.pinCode}
                  </p>
                  {orderDetails.shippingAddress?.nearbyLocation && (
                    <p className="text-gray-500 dark:text-gray-400">
                      {t("orders.orderDetails.near")}{" "}
                      {orderDetails.shippingAddress.nearbyLocation}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Store Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="section rounded-xl shadow-sm"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <Store className="w-5 h-5 mr-2" />
                  {t("orders.orderDetails.storeDetails")}
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {orderDetails.mart?.name}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    {orderDetails.mart?.address}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    {orderDetails.mart?.city}, {orderDetails.mart?.state} -{" "}
                    {orderDetails.mart?.pinCode}
                  </p>
                  {orderDetails.mart?.mobile && (
                    <div className="flex items-center space-x-2 pt-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-600 dark:text-gray-300">
                        {orderDetails.mart.mobile}
                      </span>
                    </div>
                  )}
                  <p className="text-gray-500 dark:text-gray-400">
                    {t("orders.orderDetails.hours")}{" "}
                    {formatIndianTime(orderDetails.mart?.startTime)} -{" "}
                    {formatIndianTime(orderDetails.mart?.endTime)}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Payment & Order Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Payment Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="section rounded-xl shadow-sm"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  {t("orders.orderDetails.paymentDetails")}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t("orders.orderDetails.paymentMethodLabel")}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 uppercase">
                      {orderDetails.paymentMethod}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t("orders.orderDetails.paymentStatusLabel")}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                      {orderDetails.paymentStatus}
                    </span>
                  </div>
                  {orderDetails.paymentMethod === "cod" && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">
                          {t("orders.orderDetails.cashCollected")}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          ₹{formatPrice(orderDetails.cashCollected)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">
                          {t("orders.orderDetails.cashReturned")}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          ₹{formatPrice(orderDetails.cashReturned)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Price Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="section rounded-xl shadow-sm"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <Receipt className="w-5 h-5 mr-2" />
                  {t("orders.orderDetails.priceBreakdown")}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t("orders.orderDetails.subtotal")}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      ₹
                      {formatPrice(
                        orderDetails.totalAmount -
                        (orderDetails.deliveryCharge || 0)
                      )}
                    </span>
                  </div>
                  {orderDetails.deliveryCharge > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("orders.orderDetails.deliveryChargeLabel")}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        ₹{formatPrice(orderDetails.deliveryCharge)}
                      </span>
                    </div>
                  )}
                  {orderDetails.couponDiscountAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("orders.orderDetails.couponDiscount")}
                        {orderDetails.couponCode ? ` (${orderDetails.couponCode})` : ""}
                      </span>
                      <span className="text-green-600 dark:text-green-400">
                        -₹{formatPrice(orderDetails.couponDiscountAmount)}
                      </span>
                    </div>
                  )}
                  {orderDetails.voucherDiscountAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">
                        {t("orders.orderDetails.voucherDiscount")}
                        {orderDetails.voucherCode ? ` (${orderDetails.voucherCode})` : ""}
                      </span>
                      <span className="text-green-600 dark:text-green-400">
                        -₹{formatPrice(orderDetails.voucherDiscountAmount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-600 font-semibold text-lg">
                    <span className="text-gray-900 dark:text-gray-100">
                      {t("orders.orderDetails.totalAmount")}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      ₹{formatPrice(orderDetails.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Delivery Partner Information */}
          {orderDetails.deliveryPartnerId && orderDetails.orderStatus !== OrderStatus.DELIVERED && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="section rounded-xl shadow-sm mb-6"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <Truck className="w-5 h-5 mr-2" />
                  {t("orders.orderDetails.deliveryPartner") || "Delivery Partner"}
                </h3>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {orderDetails.deliveryPartnerId.name}
                    </p>
                    {orderDetails.deliveryPartnerId.mobile && (
                      <a
                        href={`tel:${orderDetails.deliveryPartnerId.mobile}`}
                        className="flex items-center space-x-2 pt-1 text-blue-600 dark:text-blue-400"
                      >
                        <Phone className="w-4 h-4" />
                        <span className="text-sm">
                          {orderDetails.deliveryPartnerId.mobile}
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}


          {/* Additional Information */}
          {orderDetails.user?.role === "DELIVERY_PERSON" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="section rounded-xl shadow-sm mb-6"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  {t("orders.orderDetails.deliveryPerson")}
                </h3>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {orderDetails.user?.name ||
                        t("orders.orderDetails.deliveryPerson")}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {orderDetails.user?.mobile}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Order Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="section rounded-xl shadow-sm"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                {t("orders.orderDetails.orderTimeline")}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Package className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {t("orders.orderDetails.orderPlaced")}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDetailedDate(orderDetails.createdAt)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {t("orders.orderDetails.orderedVia")}{" "}
                      {orderDetails.orderedByPlatform}
                    </p>
                  </div>
                </div>

                {orderDetails.createdAt !== orderDetails.updatedAt && (
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                      {getStatusIcon(orderDetails.orderStatus)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {t("orders.orderDetails.order")}{" "}
                        {t(`orders.orderDetails.${orderDetails.orderStatus}`)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDetailedDate(orderDetails.updatedAt)}
                      </p>
                      {orderDetails.orderUpdatedByRole && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {t("orders.orderDetails.updatedBy")}{" "}
                          {orderDetails.orderUpdatedByRole}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </PullToRefresh>

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <CancelOrderModal
          isOpen={true}
          onClose={closeCancelModal}
          onConfirm={handleCancelOrder}
        />
      )}

      {/* Return Order Modal */}
      {showReturnModal && (
        <ReturnOrderModal
          isOpen={true}
          onClose={closeReturnModal}
          orderId={orderDetails._id}
          orderProducts={orderDetails.products}
          onReturnSuccess={() => {
            fetchOrderDetails();
            closeReturnModal();
          }}
        />
      )}
    </div>
  );
};

export default OrderDetailsPage;
